import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { PaginatorModule } from 'primeng/paginator';
import { CardModule } from 'primeng/card';
import { SkeletonModule } from 'primeng/skeleton';
import { UserMenuComponent } from '../../shared/user-menu/user-menu.component';
import { RegionFormModalComponent } from './region-form-modal/region-form-modal.component';
import { OrganizationFormModalComponent } from './organization-form-modal/organization-form-modal.component';

import { Organization } from '../../model/organization/organization.model';
import { Region } from '../../model/region/region.model';

import { RegionService } from '../../model/region/region.service';
import { OrganizationService } from '../../model/organization/organization.service';
import { PermissionService } from '../../permissions.service';
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { LocalizationService } from '../../shared/localization.service';

@Component({
  selector: 'app-organizations',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    PaginatorModule,
    CardModule,
    SkeletonModule,
    UserMenuComponent,
    RegionFormModalComponent,
    OrganizationFormModalComponent,
  ],
  templateUrl: './organizations.component.html',
  styleUrls: ['./organizations.component.scss']
})
export class OrganizationsComponent implements OnInit {
  @ViewChild(RegionFormModalComponent) regionFormModal!: RegionFormModalComponent;
  @ViewChild(OrganizationFormModalComponent) organizationFormModal!: OrganizationFormModalComponent;

  regions: Region[] = [];
  organizations: Organization[] = [];
  expandedRows: { [key: string]: boolean } = {};

  totalRecords = 0;
  pageSize = 10;
  currentPage = 1;
  currentPageReportTemplate = '';

  canViewRegions = false;
  canCreateRegions = false;
  canCreateOrganizations = false;
  canEditRegions = false;
  canEditOrganizations = false;
  canDeleteRegions = false;
  canDeleteOrganizations = false;

  pageSizeOptions = [
    { label: $localize`:@@pageSize10:10`, value: 10 },
    { label: $localize`:@@pageSize25:25`, value: 25 },
    { label: $localize`:@@pageSize50:50`, value: 50 },
    { label: $localize`:@@pageSize100:100`, value: 100 }
  ];

  constructor(
    private route: ActivatedRoute,
    private regionService: RegionService,
    private organizationService: OrganizationService,
    private permissionService: PermissionService,
    public localizationService: LocalizationService
  ) { }

  ngOnInit(): void {
    this.canViewRegions = this.permissionService.can('view:regions');
    this.canCreateRegions = this.permissionService.can('create:regions');
    this.canCreateOrganizations = this.permissionService.can('create:organizations');
    this.canEditRegions = this.permissionService.can('edit:regions');
    this.canEditOrganizations = this.permissionService.can('edit:organizations');
    this.canDeleteRegions = this.permissionService.can('delete:regions');
    this.canDeleteOrganizations = this.permissionService.can('delete:organizations');

    this.route.data.subscribe(data => {
      if (this.canViewRegions) {
        this.regions = data['regions']
        this.totalRecords = this.regions.length
        if (this.regions) {
          this.regions.map(async region => {
            region.organizations = await this.regionService.fetchAllForRegionId(region.id)
          })
        }
      } else {
        this.organizations = data['organizations']
        this.totalRecords = this.organizations.length
      }
      this.updateCurrentPageReport()
    })
  }

  private async refreshRegions(): Promise<void> {
    try {
      this.regions = await this.regionService.fetchAll();
      this.totalRecords = this.regions.length;
      if (this.regions) {
        this.regions.map(async region => {
          region.organizations = await this.regionService.fetchAllForRegionId(region.id)
        })
      }
      this.updateCurrentPageReport();
    } catch (error) {
      console.error(error);
    }
  }

  private async refreshOrganizations(): Promise<void> {
    try {
      this.organizations = await this.organizationService.fetchAll();
      this.totalRecords = this.organizations.length;
      this.updateCurrentPageReport();
    } catch (error) {
      console.error(error);
    }
  }

  onPageChange(event: any): void {
    this.currentPage = event.first / event.rows + 1;
    this.pageSize = event.rows;
    this.updateCurrentPageReport();
  }

  onPageSizeChange(event: any) {
    this.pageSize = event.value;
    this.currentPage = 1;
    this.updateCurrentPageReport();
  }
  updateCurrentPageReport() {
    const first = this.localizationService.localizeNumber((this.currentPage - 1) * this.pageSize + 1);
    const last = this.localizationService.localizeNumber(Math.min(this.currentPage * this.pageSize, this.totalRecords));
    const total = this.localizationService.localizeNumber(this.totalRecords);

    this.currentPageReportTemplate =
      $localize`:@@paginationReport:Showing ${first}:first: to ${last}:last: of ${total}:total: entries`;
  }

  addNewRegion() {
    this.regionFormModal.show();
  }

  addNewOrganization() {
    this.organizationFormModal.show();
  }

  editRegion(region: Region) {
    this.regionFormModal.show(region);
  }

  async deleteRegion(region: Region) {
    await this.regionService.delete(region.id);
    await this.refreshRegions();
  }

  editOrganization(org: Organization) {
    this.organizationFormModal.show(org);
  }

  async deleteOrganization(org: Organization) {
    await this.organizationService.delete(org.id);
    if (this.canViewRegions) {
      await this.refreshRegions();
    } else {
      await this.refreshOrganizations();
    }
  }

  async onSaveRegion(region: Region) {
    try {
      if (region.id) {
        await this.regionService.update(region);
      } else {
        await this.regionService.create(region);
      }
      await this.refreshRegions();
    } catch (error) {
      console.error(error);
    }
  }

  async onSaveOrganization(org: Organization) {
    try {
      if (org.id) {
        await this.organizationService.update(org);
      } else {
        await this.organizationService.create(org);
      }
      if (this.canViewRegions) {
        await this.refreshRegions();
      } else {
        await this.refreshOrganizations();
      }
    } catch (error) {
      console.error(error);
    }
  }

  getFlagEmoji(locale: string): string {
    switch (locale.toLowerCase()) {
      case 'en': return '🇺🇸';
      case 'ar': return '🇸🇦';
      case 'ru': return '🇷🇺';
      default: return '';
    }
  }
}
